/**
 * The one HTTP transport for `apps/admin` (ADR-0056).
 *
 * Before this there were eight: seven `<module>/api.ts` files plus
 * `auth/auth-client.ts`, each with its own error class, its own
 * `mapErrorCode(status)` and its own `request<T>`. They were not quite
 * copies of each other, which was the actual problem — `cohorts/api.ts`
 * had independently discovered that a Pydantic 422 returns `detail` as an
 * ARRAY, so the other seven rendered `[object Object]` in front of an
 * operator for every validation error.
 *
 * What belongs here: anything true of every admin request — the session
 * cookie, the base URL, status → code, and unwrapping the error body.
 * What stays in a module: its typed endpoints, its `detail` code →
 * operator-message mapping, and its `…_ERROR_FIELD` map. A new module adds
 * those and nothing else. It never adds a second `fetch(` — enforced by
 * `test/api/no-second-client.test.ts`.
 */
import { appRefreshSafety, appCompatibility } from '../app-update-safety.js'

import { apiBaseUrl } from '../config/runtime-environment'

/**
 * The union of what the eight clients each distinguished, so no module
 * loses a case it used to switch on.
 *
 * `RATE_LIMITED` and `PROVIDER_ERROR` came from email-test-send (429 and
 * the deliberate 502 passthrough); `BOT_FAILED` from the i18n routes,
 * where it is a payload code rather than a status.
 */
export type AdminApiErrorCode =
  | 'BAD_REQUEST'
  | 'UNAUTHENTICATED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'UNPROCESSABLE'
  | 'RATE_LIMITED'
  | 'PROVIDER_ERROR'
  | 'BOT_FAILED'
  | 'SERVER_ERROR'
  | 'NETWORK'

const ERROR_CODES: readonly AdminApiErrorCode[] = [
  'BAD_REQUEST',
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'NOT_FOUND',
  'CONFLICT',
  'UNPROCESSABLE',
  'RATE_LIMITED',
  'PROVIDER_ERROR',
  'BOT_FAILED',
  'SERVER_ERROR',
  'NETWORK',
]

/**
 * Every failure the admin SPA can see, in one class.
 *
 * `detail` is the server's *stable* code (`slug_already_exists`), which is
 * the part the UI is allowed to switch on — `message` is prose and may be
 * reworded server-side without warning. `common/field-errors.tsx` reads
 * `detail` to decide which field a failure belongs to, and duck-types on
 * the shape rather than importing this class, so it needs no per-module
 * knowledge.
 */
export class AdminApiError extends Error {
  constructor(
    public readonly code: AdminApiErrorCode,
    message: string,
    public readonly status?: number,
    /** The server's stable `detail` code, when it sent one. */
    public readonly detail?: string,
    /**
     * The raw object-shaped `detail`, when the server sent one. Carries
     * whatever rode alongside `{code, message}` — the batch routes put
     * `rows` (0-based payload indexes) and `emails` there so a form can
     * anchor the failure to the operator's own list. Untyped on purpose:
     * the consumer that knows the route narrows it.
     */
    public readonly payload?: unknown,
  ) {
    super(message)
    this.name = 'AdminApiError'
  }
}

export function mapErrorCode(status: number): AdminApiErrorCode {
  if (status === 400) return 'BAD_REQUEST'
  if (status === 401) return 'UNAUTHENTICATED'
  if (status === 403) return 'FORBIDDEN'
  if (status === 404) return 'NOT_FOUND'
  if (status === 409) return 'CONFLICT'
  if (status === 422) return 'UNPROCESSABLE'
  if (status === 429) return 'RATE_LIMITED'
  if (status === 502) return 'PROVIDER_ERROR'
  return 'SERVER_ERROR'
}

/** A structured `detail`, as the email-test-send 502 passthrough sends. */
interface DetailObject {
  code?: string
  message?: string
}

interface ErrorBody {
  /** String on the provisioning routes, array on a Pydantic 422, object on a 502. */
  detail?: unknown
  message?: string
  /** Payload-level code, used by the /admin/i18n routes. */
  code?: string
}

/**
 * The server has four error-body shapes, and each of the old clients knew
 * about a different subset. Resolved here once, in precedence order:
 *
 * 1. `detail` as a **string** — a stable code. Message and code both.
 * 2. `detail` as an **array** — Pydantic 422. First `msg` wins; there is no
 *    stable code, so `detail` stays unset and the failure renders at the
 *    form rather than being matched against a field map.
 * 3. `detail` as an **object** — the 502 provider passthrough, `{code, message}`.
 * 4. top-level `message` — the /admin/i18n convention.
 *
 * `translations/api.ts` previously read only (4), so a FastAPI `detail`
 * reached the operator as "Request failed: 403". It now gets (1)–(3) for
 * free. That is a deliberate improvement of the same kind as the 422 fix,
 * not a regression, and no spec asserted the old behavior.
 */
function resolveError(
  body: ErrorBody,
  status: number,
): { message: string; detail?: string; payload?: unknown } {
  const fallback = `Request failed: ${status}`
  const { detail } = body

  if (typeof detail === 'string') {
    return { message: detail, detail }
  }
  if (Array.isArray(detail)) {
    const first = (detail[0] as { msg?: string } | undefined)?.msg
    return { message: first ?? body.message ?? fallback }
  }
  if (detail !== null && typeof detail === 'object') {
    const { code, message } = detail as DetailObject
    return {
      message: message ?? code ?? body.message ?? fallback,
      detail: code,
      // The whole object rides along: the batch routes carry `rows` and
      // `emails` beside {code, message}, and dropping them here would
      // leave the form nothing to anchor a row failure to.
      payload: detail,
    }
  }
  return { message: body.message ?? fallback }
}

/**
 * A payload `code` is trusted only when it is one we already model.
 *
 * This is what preserves the i18n routes' `BOT_FAILED` — a 500 the operator
 * responds to by retrying the bot, not by filing a bug — without letting an
 * arbitrary server string become an error code the UI cannot switch on.
 */
function codeFor(body: ErrorBody, status: number): AdminApiErrorCode {
  const payloadCode = body.code
  if (
    payloadCode !== undefined &&
    (ERROR_CODES as readonly string[]).includes(payloadCode)
  ) {
    return payloadCode as AdminApiErrorCode
  }
  return mapErrorCode(status)
}

/**
 * Send one authenticated admin request and return the successful response
 * without assuming its body is JSON.
 *
 * Audio downloads use this seam because parsing a WAV as JSON would fail.
 * Keeping the raw-response variant here preserves the one-transport rule:
 * cookies, base URL, network failures, and all server error shapes stay
 * identical for JSON and binary endpoints.
 */
export async function requestResponse(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  let response: Response
  try {
    const headers = appCompatibility.headers(init.headers)
    if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json')
    // One stable key per browser request. A proxy/network retry preserves it,
    // while a new operator click gets a new intent. Recipient scoping on the
    // server prevents the same inbound request id colliding across targets.
    if ((init.method ?? 'GET').toUpperCase() !== 'GET' && !headers.has('X-Request-ID')) {
      headers.set('X-Request-ID', crypto.randomUUID())
    }
    response = await fetch(`${apiBaseUrl()}${path}`, {
      ...init,
      credentials: 'include',
      headers,
    })
  } catch (err) {
    // No status: nothing answered. Distinguishing this from a 5xx is what
    // lets a page say "you may be offline" rather than "the server broke".
    throw new AdminApiError(
      'NETWORK',
      err instanceof Error ? err.message : 'network error',
    )
  }

  await appCompatibility.receive(response)

  if (response.ok) return response

  let body: ErrorBody = {}
  try {
    body = (await response.json()) as ErrorBody
  } catch {
    /* non-JSON body — an nginx error page, say. The status is all we have. */
  }
  const { message, detail, payload } = resolveError(body, response.status)
  throw new AdminApiError(
    codeFor(body, response.status),
    message,
    response.status,
    detail,
    payload,
  )
}

async function unguardedRequest<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const response = await requestResponse(path, init)
  // 204 on delete and set-password. Parsing it would throw.
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const operation = () => unguardedRequest<T>(path, init)
  return appRefreshSafety.guard(init, operation)
}

/** Existing API transport owns public compatibility discovery too. */
export async function readApiCompatibility(signal: AbortSignal): Promise<unknown> {
  const body = await request<{ clientCompatibility?: unknown }>('/version', { signal, cache: 'no-store' })
  return body.clientCompatibility
}
